import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, ArrowLeft, FileText, Lock, Server, 
  CheckCircle2, Database, Mail, UserCheck, 
  Building2, Briefcase, Key, LockKeyhole, MessageSquare, Send, Sparkles
} from 'lucide-react';

const Politica = ({ language }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('comercial'); // 'comercial' | 'vacantes' | 'seguridad' | 'derechos'

  const t = {
    es: {
      back: "Volver al Inicio",
      badge: "COMPLIANCE & PRIVACIDAD 2026",
      title: "Política de Protección de Datos e Información",
      subtitle: "Transparencia absoluta. Seguridad corporativa. Cero letras chiquitas.",
      lastUpdate: "Vigencia Oficial: 2026",
      introTitle: "En Zaphy, tu tranquilidad y privacidad son nuestro mayor compromiso",
      introDesc: "Sabemos que al confiar en nosotros la Gestión Humana, la Nómina o el SG-SST de tu empresa (o al enviarnos tu Hoja de Vida), nos estás entregando información valiosa. Tratamos tus datos personales con absoluta reserva y bajo los estándares de la Ley 1581 de 2012 y el Decreto 1377 de 2013.",
      
      tab1: "Comercial, WhatsApp y Promociones",
      tab2: "Hojas de Vida y Selección",
      tab3: "Infraestructura & TI",
      tab4: "Derechos del Titular (ARCO)",

      // Sección Comercial Ampliada y Corregida
      comercialTitle: "1. Tratamiento de Datos Comerciales, Promociones y Canales Directos",
      comercialDesc: "Al registrarte en nuestros formularios web, solicitar un diagnóstico o chatear con nosotros por WhatsApp, autorizas a Zaphy Gestión Integral S.A.S. a utilizar tus datos de contacto para:",
      comercialBullets: [
        "Envío de promociones exclusivas y ofertas especiales en nuestros servicios de Outsourcing de Nómina, Gestión Humana, SG-SST y Selección de Personal.",
        "Notificaciones directas vía WhatsApp y correo electrónico sobre las últimas publicaciones, guías y artículos de nuestro Blog Corporativo.",
        "Invitaciones prioritarias a eventos presenciales, mentorías directas y conferencias empresariales organizadas junto a aliados como FENALCO Antioquia.",
        "Entrega de cotizaciones personalizadas y reportes de diagnóstico de blindaje laboral sin costo para tu empresa."
      ],
      comercialNote: "🔒 Promesa de Cero Spam: Respetamos tu tiempo y tu espacio. Solo te enviaremos contenido útil, ofertas de alto valor e información que ayude a crecer tu empresa. Puedes cancelar tu suscripción en cualquier momento.",

      // Sección Vacantes / HV
      vacantesTitle: "2. Tratamiento para Candidatos y Hojas de Vida (Talento Zaphy)",
      vacantesDesc: "Si aplicas a una vacante activa o envías tu Hoja de Vida a nuestro banco de talento para procesos de Selección de Personal, garantizamos un tratamiento confidencial y ético:",
      vacantesBullets: [
        "Evaluación de perfil profesional y verificación de antecedentes académicos y laborales.",
        "Citación a entrevistas virtuales o presenciales, pruebas psicotécnicas y evaluaciones de competencias.",
        "Presentación de tu perfil profesional ante empresas clientes en procesos de selección outsourced.",
        "Inclusión en nuestra base de datos confidencial para futuras oportunidades laborales alineadas con tu experiencia."
      ],
      vacantesNote: "📄 Retención Garantizada: Conservamos tu Hoja de Vida por un periodo máximo de 24 meses para futuras vacantes, tras lo cual será eliminada de forma segura salvo que solicites su eliminación antes.",

      // Sección Ciberseguridad
      secTitle: "3. Infraestructura y Protocolos de Seguridad TI",
      secDesc: "Protegemos la información de tu empresa con los más altos estándares de seguridad informática.",
      securityFeatures: [
        { title: "Cifrado de Extremo a Extremo", desc: "Transmisión de información protegida mediante cifrado SSL/TLS 1.3 de grado bancario.", icon: Lock },
        { title: "Servidores ISO 27001", desc: "Bases de datos alojadas en infraestructura en la nube con respaldo diario y redundancia.", icon: Server },
        { title: "Control de Acceso Restringido", desc: "Autenticación multifactor y política de mínimo privilegio para el personal autorizado de Zaphy.", icon: Key },
        { title: "Respaldo Automatizado", desc: "Copias de seguridad diarias para garantizar la continuidad del negocio y la integridad de tus archivos.", icon: Database }
      ],

      // Sección Derechos
      arcoTitle: "4. Tus Derechos como Titular (Conocer, Actualizar, Rectificar, Suprimir)",
      arcoDesc: "Como propietario de tus datos, tienes derecho a solicitar en cualquier momento la consulta, modificación o eliminación total de tu información de nuestras bases de datos.",
      contactLegal: "Oficial de Protección de Datos Personales:",
      emailLegal: "comercial@zaphy.com.co",
      sicNote: "Vigilancia Legal: El tratamiento de datos personales en Zaphy está sujeto a la supervisión de la Superintendencia de Industria y Comercio (SIC) conforme a la legislación colombiana (Ley 1581 de 2012)."
    },
    en: {
      back: "Back to Home",
      badge: "COMPLIANCE & PRIVACY 2026",
      title: "Data Protection & Privacy Policy",
      subtitle: "Absolute transparency. Enterprise security. Zero fine print.",
      lastUpdate: "Official Validity: 2026",
      introTitle: "At Zaphy, your peace of mind and privacy are our highest commitment",
      introDesc: "We process your data strictly under Colombian Law 1581 of 2012 with the highest cybersecurity standards.",
      
      tab1: "Commercial, WhatsApp & Promotions",
      tab2: "Resumes & Recruitment",
      tab3: "IT Infrastructure",
      tab4: "Data Subject Rights",

      comercialTitle: "1. Commercial Data Processing, Promotions & Direct Channels",
      comercialDesc: "When filling out web forms or contacting us via WhatsApp, you authorize Zaphy to use your contact details for:",
      comercialBullets: [
        "Sending exclusive promotions and special offers on Payroll, HR, SST, and Recruitment Outsourcing services.",
        "Direct WhatsApp and Email notifications regarding new Corporate Blog articles and practical guides.",
        "Priority invitations to corporate events, mentorships, and conferences with partners like FENALCO.",
        "Delivery of customized commercial proposals and free business risk diagnostic reports."
      ],
      comercialNote: "🔒 Zero Spam Promise: We respect your time. We will only send high-value content and promotions to help your business grow. You can unsubscribe at any time.",

      vacantesTitle: "2. Data Processing for Job Applicants & Resumes",
      vacantesDesc: "If you submit your resume for job openings, we guarantee ethical and confidential handling:",
      vacantesBullets: [
        "Professional qualification assessment and background check verification.",
        "Scheduling interviews, skills testing, and psychometric assessments.",
        "Submitting candidate profiles to client companies for outsourced recruitment.",
        "Inclusion in our talent database for future job opportunities."
      ],
      vacantesNote: "📄 Data Retention: Resumes are kept securely for up to 24 months for future job openings.",

      secTitle: "3. IT Infrastructure & Protocols",
      secDesc: "We protect your corporate data with maximum security protocols.",
      securityFeatures: [
        { title: "End-to-End Encryption", desc: "Protected data transmission via bank-grade SSL/TLS 1.3 protocols.", icon: Lock },
        { title: "ISO 27001 Cloud Servers", desc: "Databases hosted on certified infrastructure with daily backups.", icon: Server },
        { title: "Restricted Access Control", desc: "Multi-factor authentication and strict access limits for authorized personnel.", icon: Key },
        { title: "Automated Backups", desc: "Daily backups to guarantee data integrity and business continuity.", icon: Database }
      ],

      arcoTitle: "4. Your Rights (Access, Rectify, Delete)",
      arcoDesc: "You can request access, update, or total deletion of your personal data at any time.",
      contactLegal: "Data Protection Officer:",
      emailLegal: "comercial@zaphy.com.co",
      sicNote: "Legal Oversight: Data processing at Zaphy is subject to supervision by the Superintendency of Industry and Commerce (SIC)."
    }
  };

  const text = t[language] || t.es;
  const zaphyLogoBlue = "https://zaphy.com.co/wp-content/uploads/2025/05/Logo-Zaphy-TX-Blanco-logo-azul.png";

  return (
    <div className="pt-28 pb-12 bg-slate-50 font-[Poppins] min-h-screen text-slate-800 animate-[fadeIn_0.5s_ease-out]">
      
      {/* HERO HEADER LEGAL ULTRA MODERNO */}
      <div className="bg-slate-950 text-white py-16 md:py-20 relative overflow-hidden border-b border-purple-900/30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/15 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-10 w-80 h-80 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <button 
            onClick={() => navigate('/')} 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors font-semibold mb-8 text-xs uppercase tracking-widest bg-slate-900/80 px-4 py-2 rounded-full border border-slate-800"
          >
            <ArrowLeft size={16} /> {text.back}
          </button>

          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="max-w-2xl">
              <span className="inline-block bg-purple-600/90 text-white text-[11px] font-black px-3.5 py-1 rounded-full uppercase tracking-widest mb-4 shadow-sm">
                {text.badge}
              </span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-4 text-white">
                {text.title}
              </h1>
              <p className="text-lg text-slate-300 font-light leading-relaxed">
                {text.subtitle}
              </p>
              
              <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-green-500/10 text-green-400 text-xs font-bold border border-green-500/20">
                <CheckCircle2 size={14} /> {text.lastUpdate}
              </div>
            </div>

            {/* BRANDING ZAPHY GUARDIÁN CON LOGO OFICIAL */}
            <div className="relative shrink-0 hidden md:block">
              <div className="w-52 h-52 rounded-full bg-gradient-to-tr from-purple-600/30 to-cyan-500/30 p-2 backdrop-blur-md flex items-center justify-center shadow-2xl">
                <div className="w-full h-full bg-slate-900 rounded-full flex flex-col items-center justify-center p-4 text-center border border-slate-700 relative overflow-hidden">
                  <ShieldCheck size={100} className="text-cyan-400/10 absolute pointer-events-none" />
                  <img 
                    src={zaphyLogoBlue} 
                    alt="Zaphy Logo Oficial" 
                    className="h-12 w-auto object-contain relative z-10 mb-2"
                  />
                  <span className="text-[10px] text-cyan-400 font-bold uppercase tracking-widest relative z-10">
                    Tranquilidad Garantizada
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONTENEDOR PRINCIPAL CON TABS DE NAVEGACIÓN */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        
        {/* INTRODUCCIÓN GENERAL DE CONFIANZA */}
        <div className="bg-white rounded-3xl p-6 md:p-10 shadow-xl shadow-slate-200/50 border border-slate-100 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center shrink-0 shadow-inner">
              <LockKeyhole size={28} />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-black text-slate-900 mb-2">
                {text.introTitle}
              </h2>
              <p className="text-slate-600 leading-relaxed text-sm md:text-base text-justify">
                {text.introDesc}
              </p>
            </div>
          </div>
        </div>

        {/* NAVEGACIÓN POR PESTAÑAS (TABS INTERACTIVOS) */}
        <div className="flex flex-wrap gap-2 mb-8 bg-slate-200/60 p-1.5 rounded-2xl border border-slate-200">
          <button
            onClick={() => setActiveTab('comercial')}
            className={`flex-1 min-w-[160px] py-3 px-4 rounded-xl text-xs md:text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'comercial'
                ? 'bg-purple-700 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Building2 size={16} /> {text.tab1}
          </button>

          <button
            onClick={() => setActiveTab('vacantes')}
            className={`flex-1 min-w-[160px] py-3 px-4 rounded-xl text-xs md:text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'vacantes'
                ? 'bg-purple-700 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Briefcase size={16} /> {text.tab2}
          </button>

          <button
            onClick={() => setActiveTab('seguridad')}
            className={`flex-1 min-w-[160px] py-3 px-4 rounded-xl text-xs md:text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'seguridad'
                ? 'bg-purple-700 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Server size={16} /> {text.tab3}
          </button>

          <button
            onClick={() => setActiveTab('derechos')}
            className={`flex-1 min-w-[160px] py-3 px-4 rounded-xl text-xs md:text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'derechos'
                ? 'bg-purple-700 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <UserCheck size={16} /> {text.tab4}
          </button>
        </div>

        {/* CONTENIDO DINÁMICO SEGÚN PESTAÑA */}
        <div className="bg-white rounded-3xl p-6 md:p-12 shadow-xl shadow-slate-200/50 border border-slate-100 mb-8">
          
          {/* TAB 1: COMERCIAL, WHATSAPP & PROMOCIONES */}
          {activeTab === 'comercial' && (
            <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
              <h3 className="text-2xl font-extrabold text-slate-900 border-b border-slate-100 pb-4">
                {text.comercialTitle}
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                {text.comercialDesc}
              </p>
              
              <ul className="space-y-3 font-medium text-slate-700 text-sm md:text-base">
                {text.comercialBullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3 bg-purple-50/50 p-3.5 rounded-xl border border-purple-100/60">
                    <Sparkles size={18} className="text-purple-600 shrink-0 mt-0.5" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              <div className="p-5 rounded-2xl bg-slate-900 text-slate-200 text-xs md:text-sm font-medium border border-slate-800 flex items-center gap-3">
                <MessageSquare size={24} className="text-cyan-400 shrink-0" />
                <p>{text.comercialNote}</p>
              </div>
            </div>
          )}

          {/* TAB 2: VACANTES Y HOJAS DE VIDA */}
          {activeTab === 'vacantes' && (
            <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
              <h3 className="text-2xl font-extrabold text-slate-900 border-b border-slate-100 pb-4">
                {text.vacantesTitle}
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                {text.vacantesDesc}
              </p>
              
              <ul className="space-y-3 font-medium text-slate-700 text-sm md:text-base">
                {text.vacantesBullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                    <CheckCircle2 size={18} className="text-purple-600 shrink-0 mt-0.5" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              <div className="p-5 rounded-2xl bg-purple-50 text-purple-900 text-xs md:text-sm font-medium border border-purple-200 flex items-center gap-3">
                <FileText size={24} className="text-purple-700 shrink-0" />
                <p>{text.vacantesNote}</p>
              </div>
            </div>
          )}

          {/* TAB 3: CIBERSEGURIDAD TI */}
          {activeTab === 'seguridad' && (
            <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
              <div className="mb-6">
                <h3 className="text-2xl font-extrabold text-slate-900 mb-2">
                  {text.secTitle}
                </h3>
                <p className="text-slate-600 text-sm">
                  {text.secDesc}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {text.securityFeatures.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80 hover:border-purple-300 transition-all">
                      <div className="w-10 h-10 bg-purple-100 text-purple-700 rounded-xl flex items-center justify-center mb-3">
                        <Icon size={20} />
                      </div>
                      <h4 className="font-bold text-slate-900 text-base mb-1">{item.title}</h4>
                      <p className="text-xs md:text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 4: DERECHOS ARCO & VIGILANCIA SIC */}
          {activeTab === 'derechos' && (
            <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
              <h3 className="text-2xl font-extrabold text-slate-900 border-b border-slate-100 pb-4">
                {text.arcoTitle}
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed text-justify">
                {text.arcoDesc}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center">
                  <span className="font-bold text-purple-700 text-sm block mb-1">Ley 1581 de 2012</span>
                  <span className="text-xs text-slate-500">Régimen General de Protección de Datos</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center">
                  <span className="font-bold text-purple-700 text-sm block mb-1">Decreto 1377 de 2013</span>
                  <span className="text-xs text-slate-500">Reglamentación de Autorizaciones</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center">
                  <span className="font-bold text-purple-700 text-sm block mb-1">Vigilancia SIC</span>
                  <span className="text-xs text-slate-500">Superintendencia de Industria y Comercio</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 text-xs text-purple-900 font-medium mb-6">
                <p>{text.sicNote}</p>
              </div>

              <div className="p-6 rounded-2xl bg-gradient-to-r from-purple-900 to-indigo-900 text-white flex flex-col sm:flex-row items-center justify-between gap-4 shadow-lg">
                <div>
                  <p className="text-xs text-purple-200 uppercase tracking-wider font-bold mb-1">{text.contactLegal}</p>
                  <a href={`mailto:${text.emailLegal}`} className="text-lg font-black text-cyan-300 hover:underline flex items-center gap-1.5">
                    <Mail size={18} /> {text.emailLegal}
                  </a>
                </div>
                <a 
                  href={`mailto:${text.emailLegal}?subject=Solicitud%20Tratamiento%20de%20Datos%20Personal`}
                  className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all whitespace-nowrap flex items-center gap-2"
                >
                  <Send size={14} /> Enviar Solicitud
                </a>
              </div>
            </div>
          )}

        </div>

      </div>

    </div>
  );
};

export default Politica;